import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FiTrash2, FiPlus, FiMove, FiDownload, FiExternalLink, FiUser, FiRefreshCw } from 'react-icons/fi';
import api, { getDownloadBaseUrl, openLicencaPdf } from '../services/api';
import DocumentosSection from '../components/DocumentosSection';
import { useAuth } from '../context/AuthContext';
import './ValidarExtracao.css';


/** Mensagem amigável da etapa atual (API ou inferida dos passos técnicos). */
function inferirMensagemEtapa(log) {
  if (log?.mensagem_atual) return log.mensagem_atual;
  const passos = Array.isArray(log?.passos) ? log.passos : [];
  for (let i = passos.length - 1; i >= 0; i -= 1) {
    const p = String(passos[i] || '');
    const verif = p.match(/rodada (\d+)\/(\d+)/i);
    if (verif) return `Revisando condicionantes (${verif[1]} de ${verif[2]})...`;
    if (/Categorizando (\d+) condicionante/i.test(p)) {
      const m = p.match(/Categorizando (\d+)/i);
      return m ? `Categorizando condicionantes (1 de ${m[1]})...` : 'Categorizando condicionantes...';
    }
    if (/extração completa cumulativa|Extração completa com IA/i.test(p)) return 'Extraindo condicionantes com IA...';
    if (/Chamando IA.*documento|Documento e empreendimento extraídos/i.test(p)) return 'Extraindo dados da licença...';
    if (/OCR extraiu/i.test(p)) return 'Lendo documento com OCR...';
    if (/Condicionantes extraídas por regras/i.test(p)) return 'Identificando condicionantes no texto...';
    if (/PDF verificado: textual/i.test(p)) return 'Lendo texto do documento...';
    if (/Reprocessamento iniciado/i.test(p)) return 'Reiniciando análise por IA...';
    if (/Processamento iniciado/i.test(p)) return 'Iniciando análise da licença...';
  }
  return '';
}

/** Converte DD-MM-YYYY ou YYYY-MM-DD para YYYY-MM-DD (para input type="date") */
function toDateInputValue(val) {
  if (!val) return '';
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = s.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (!m) return '';
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}

const ValidarExtracao = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [document, setDocument] = useState({ tipo: '', numero: '', orgao_emissor: '', municipio_uf: '', processo_administrativo: '', data_emissao: '', validade: {} });
  const [empreendimentoId, setEmpreendimentoId] = useState('');
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [condicionantes, setCondicionantes] = useState([]);
  const [textoExtraido, setTextoExtraido] = useState('');
  const [extracaoLog, setExtracaoLog] = useState({});
  const [extracaoStatus, setExtracaoStatus] = useState('concluida');
  const [historico, setHistorico] = useState([]);
  const [tarefas, setTarefas] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [arquivoPdf, setArquivoPdf] = useState(null);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [usuariosEmpreendimento, setUsuariosEmpreendimento] = useState([]);
  const [addResponsavelCondIdx, setAddResponsavelCondIdx] = useState(null);
  const [addResponsavelId, setAddResponsavelId] = useState('');
  const [reprocessing, setReprocessing] = useState(false);

  const carregarExtracao = useCallback(async (opts = {}) => {
    const { silent } = opts;
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const res = await api.get(`/api/licencas/${id}/extracao`);
      const data = res.data;
      setDocument({
        tipo: data.document?.tipo ?? '',
        numero: data.document?.numero ?? '',
        orgao_emissor: data.document?.orgao_emissor ?? '',
        municipio_uf: data.document?.municipio_uf ?? '',
        processo_administrativo: data.document?.processo_administrativo ?? '',
        data_emissao: data.document?.data_emissao ?? '',
        validade: data.document?.validade ?? {}
      });
      setEmpreendimentoId(data.empreendimento_id ?? data.empreendimento?.id ?? '');
      setCondicionantes((data.condicionantes || []).map((c) => {
        const mon = c.monitoramento || {};
        return {
          ...c,
          condicionante_numero: c.condicionante_numero ?? c.id ?? '',
          frequencia: c.frequencia ?? mon.recorrencia ?? null,
          primeiro_monitoramento: c.primeiro_monitoramento ?? mon.primeiro_monitoramento ?? null,
          gerar_monitoramento: !!c.gerar_monitoramento || !!c.exige_monitoramento,
          alertas_dias_antes: 7,
          prioridade: c.prioridade ?? 'media',
          responsaveis_ids: c.responsaveis_ids || []
        };
      }));
      setTextoExtraido(data.texto_extraido || '');
      setExtracaoLog(data.extracao_log || {});
      setExtracaoStatus(data.extracao_status || 'concluida');
      setHistorico(data.historico || []);
      setTarefas(data.tarefas || []);
      setDocumentos(data.documentos || []);
      setArquivoPdf(data.arquivo_pdf || null);
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao carregar detalhes da licença');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [id]);

  const carregarDocumentos = useCallback(async () => {
    try {
      const res = await api.get(`/api/documentos?licenca_id=${id}`);
      const data = res.data;
      const list = Array.isArray(data) ? data : (data && typeof data === 'object' && 'data' in data ? (data.data || []) : []);
      setDocumentos(prev => {
        if (list.length === 0 && prev.length > 0) return prev;
        return list;
      });
    } catch (err) {
      console.error('Erro ao carregar documentos:', err);
      /* Não zerar a lista no erro para não apagar o que o usuário acabou de adicionar */
    }
  }, [id]);

  const carregarEmpreendimentos = useCallback(async () => {
    try {
      const res = await api.get('/api/empreendimentos');
      setEmpreendimentos(res.data || []);
    } catch (err) {
      console.error('Erro ao carregar empreendimentos:', err);
    }
  }, []);

  // Indica se a extração em background ainda está rodando (sem condicionantes carregadas)
  const condicoesCarregando = ['pendente', 'processando'].includes(extracaoStatus);

  useEffect(() => {
    carregarExtracao();
  }, [carregarExtracao]);

  // Recarrega a extração em background enquanto o processamento estiver pendente,
  // para atualizar dados, texto extraído e condicionantes assim que forem gravados.
  useEffect(() => {
    if (!condicoesCarregando) return;
    const interval = setInterval(() => {
      carregarExtracao({ silent: true });
    }, 1000);
    return () => clearInterval(interval);
  }, [condicoesCarregando, carregarExtracao]);

  useEffect(() => {
    carregarEmpreendimentos();
  }, [carregarEmpreendimentos]);

  useEffect(() => {
    if (!empreendimentoId) {
      setUsuariosEmpreendimento([]);
      return;
    }
    api.get(`/api/empreendimentos/${empreendimentoId}/usuarios-list`)
      .then((res) => setUsuariosEmpreendimento(res.data || []))
      .catch(() => setUsuariosEmpreendimento([]));
  }, [empreendimentoId]);

  // Progresso visual enquanto a extração em background está rodando
  useEffect(() => {
    let interval;
    if (condicoesCarregando) {
      setProcessingProgress((prev) => (prev <= 5 ? 5 : prev));
      interval = setInterval(() => {
        setProcessingProgress((prev) => {
          if (prev >= 90) return prev;
          return prev + 5;
        });
      }, 2000);
    } else if (!condicoesCarregando && condicionantes.length > 0) {
      setProcessingProgress(100);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [condicoesCarregando, condicionantes.length]);

  const handleDocChange = (field, value) => {
    setDocument((prev) => ({ ...prev, [field]: value }));
  };

  const handleValidadeChange = (field, value) => {
    setDocument((prev) => ({
      ...prev,
      validade: { ...(prev.validade || {}), [field]: value }
    }));
  };

  const handleCondChange = (index, field, value) => {
    setCondicionantes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleCondPrimeiroMonitoramentoChange = (index, subField, value) => {
    setCondicionantes((prev) => {
      const next = [...prev];
      const atual = next[index].primeiro_monitoramento || {};
      next[index] = {
        ...next[index],
        primeiro_monitoramento: { ...atual, [subField]: value || null }
      };
      return next;
    });
  };

  const handleRemoverCondicionante = (index) => {
    if (!window.confirm('Remover esta condicionante da lista?')) return;
    setCondicionantes((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAdicionarCondicionante = () => {
    const nova = {
      id: `temp-${Date.now()}`,
      condicionante_numero: String(condicionantes.length + 1),
      tipo: 'geral',
      tipo_condicao: 'geral',
      tema: '',
      texto: '',
      exige_monitoramento: false,
      gerar_monitoramento: false,
      frequencia: null,
      primeiro_monitoramento: null,
      monitoramento: null,
      source_pages: null,
      evidence_text: null,
      alertas_dias_antes: 7,
      prioridade: 'media',
      responsaveis_ids: []
    };
    setCondicionantes((prev) => [...prev, nova]);
  };

  const handleDragCondicionante = (dragIndex, dropIndex) => {
    if (dragIndex === dropIndex) return;
    setCondicionantes((prev) => {
      const next = [...prev];
      const [removido] = next.splice(dragIndex, 1);
      next.splice(dropIndex, 0, removido);
      return next;
    });
  };

  const handleSalvarDocumento = async (e) => {
    e.preventDefault();
    const campos = ['tipo', 'numero', 'orgao_emissor', 'municipio_uf', 'processo_administrativo'];
    const vazio = campos.find((f) => !document[f]?.trim());
    if (vazio) {
      alert(`O campo "${vazio.replace(/_/g, ' ')}" é obrigatório.`);
      return;
    }
    if (!document.validade?.data_fim?.trim()) {
      alert('O campo "Validade - data fim" é obrigatório.');
      return;
    }
    if (!document.data_emissao?.trim()) {
      alert('O campo "Data de emissão" é obrigatório.');
      return;
    }
    try {
      setSaving(true);
      await api.patch(`/api/licencas/${id}/documento`, {
        ...document,
        validade: document.validade || {}
      });
      alert('Dados do documento salvos.');
      carregarExtracao();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleSalvarEmpreendimento = async (e) => {
    e.preventDefault();
    if (!empreendimentoId) {
      alert('Selecione um empreendimento.');
      return;
    }
    try {
      setSaving(true);
      await api.patch(`/api/licencas/${id}/empreendimento`, { empreendimento_id: empreendimentoId });
      alert('Empreendimento vinculado.');
      carregarExtracao();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleConcluirValidacao = async (e) => {
    e.preventDefault();
    if (!empreendimentoId) {
      alert('Selecione um empreendimento antes de concluir a validação.');
      return;
    }
    const temMonitoramento = condicionantes.some((c) => c.gerar_monitoramento);
    const validadeDefinida = document.validade?.data_fim?.trim() || document.data_vencimento?.trim();
    if (temMonitoramento && !validadeDefinida) {
      alert('A licença precisa ter uma validade definida (data fim) para criar os prazos de monitoramento. Preencha o campo "Validade - data fim" na seção Dados do documento.');
      const secDoc = window.document.getElementById('sec-documento-details');
      const inputValidade = window.document.getElementById('input-validade-data-fim');
      if (secDoc) {
        secDoc.setAttribute('open', '');
        secDoc.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      if (inputValidade) {
        setTimeout(() => inputValidade.focus(), 400);
      }
      return;
    }
    try {
      setSaving(true);
      const payload = {
        condicionantes: condicionantes.map((c) => ({
          id: c.id,
          condicionante_numero: c.condicionante_numero ?? c.id,
          tipo: c.tipo_condicao ?? c.tipo,
          tema: c.tema,
          texto: c.texto,
          exige_monitoramento: c.gerar_monitoramento,
          frequencia: c.frequencia,
          primeiro_monitoramento: c.primeiro_monitoramento,
          gerar_monitoramento: c.gerar_monitoramento,
          alertas_dias_antes: c.gerar_monitoramento ? (c.alertas_dias_antes ?? 7) : null,
          prioridade: c.prioridade ?? 'media',
          responsaveis_ids: c.responsaveis_ids || []
        }))
      };
      await api.put(`/api/licencas/${id}/extracao`, payload);
      alert('Dados validados e monitoramentos criados com sucesso.');
      carregarExtracao();
    } catch (err) {
      alert(err.response?.data?.error || 'Erro ao concluir validação');
    } finally {
      setSaving(false);
    }
  };

  const handleReprocessar = async () => {
    if (!window.confirm('Reprocessar esta licença com IA usando o PDF já salvo?')) return;
    try {
      setReprocessing(true);
      const res = await api.post(`/api/licencas/${id}/reprocessar`);
      setProcessingProgress(5);
      await carregarExtracao({ silent: true });
      alert(res.data?.message || 'Reprocessamento da licença iniciado.');
    } catch (err) {
      alert(err.response?.data?.error || 'Não foi possível reprocessar a licença.');
    } finally {
      setReprocessing(false);
    }
  };

  const errosExtracao = Array.isArray(extracaoLog?.erros) ? extracaoLog.erros.filter(Boolean) : [];
  const avisosExtracao = Array.isArray(extracaoLog?.avisos) ? extracaoLog.avisos.filter(Boolean) : [];
  const mensagemEtapa = inferirMensagemEtapa(extracaoLog);

  if (loading) {
    return (
      <div className="validar-extracao-page">
        <div className="loading" aria-busy="true">Carregando detalhes da licença</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="validar-extracao-page">
        <div className="error-state">
          <p>{error}</p>
          <button type="button" className="btn btn-primary" onClick={() => navigate('/licencas')}>
            Voltar às licenças
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="validar-extracao-page">
      <header className="page-header">
        <h1 className="page-title">Detalhes da licença</h1>
        <div className="page-header-actions">
          {arquivoPdf && hasPermission('license', 'edit') && (
            <button
              type="button"
              className="btn btn-secondary"
              title="Executar novamente a análise por IA"
              onClick={handleReprocessar}
              disabled={reprocessing || condicoesCarregando}
            >
              <FiRefreshCw /> {reprocessing || condicoesCarregando ? 'Processando...' : 'Reprocessar com IA'}
            </button>
          )}
          {arquivoPdf && (
            <button
              type="button"
              className="btn btn-download-pdf"
              title="Baixar PDF da licença"
              onClick={() => openLicencaPdf(arquivoPdf)}
            >
              <FiDownload /> PDF
            </button>
          )}
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/licencas')}>
            ← Voltar
          </button>
        </div>
      </header>

      {condicoesCarregando && (
        <div className="licenca-processing-banner" aria-live="polite">
          <div className="licenca-processing-title">
            Processando licença: {Math.min(Math.max(processingProgress, 5), 99)}%
          </div>
          <div className="licenca-processing-bar">
            <div
              className="licenca-processing-bar-fill"
              style={{ width: `${Math.min(Math.max(processingProgress, 5), 100)}%` }}
            />
          </div>
          {mensagemEtapa && (
            <p className="licenca-processing-step">{mensagemEtapa}</p>
          )}
        </div>
      )}

      {errosExtracao.length > 0 && !condicoesCarregando && (
        <div className="licenca-processing-error" role="alert">
          <strong>A análise por IA não pôde ser concluída.</strong>
          <span>{errosExtracao.join(' ')}</span>
          {arquivoPdf && <span>O PDF permanece salvo. Use “Reprocessar com IA” para tentar novamente.</span>}
        </div>
      )}

      {avisosExtracao.length > 0 && errosExtracao.length === 0 && !condicoesCarregando && (
        <div className="licenca-processing-warning" role="status">
          <strong>Extração concluída com observações.</strong>
          <span>{avisosExtracao.join(' ')}</span>
          <span>As condicionantes abaixo já foram carregadas — revise se algo estiver faltando ou sobrando.</span>
        </div>
      )}

      <p className="validar-intro">
        Revise os dados do documento. Selecione o empreendimento vinculado à licença (pode trocar a qualquer momento). Marque as condicionantes que devem gerar prazos de monitoramento.
      </p>

      <details id="sec-documento-details" className="extracao-section collapsible-section" aria-labelledby="sec-documento">
        <summary>
          <h2 id="sec-documento">Dados do documento (licença)</h2>
        </summary>
        <form onSubmit={handleSalvarDocumento}>
          <div className="form-grid">
            <div className="form-group">
              <label>Tipo *</label>
              <input
                type="text"
                required
                value={document.tipo}
                onChange={(e) => handleDocChange('tipo', e.target.value)}
                placeholder="Ex: Licença de Operação"
              />
            </div>
            <div className="form-group">
              <label>Número *</label>
              <input
                type="text"
                required
                value={document.numero}
                onChange={(e) => handleDocChange('numero', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Órgão emissor *</label>
              <input
                type="text"
                required
                value={document.orgao_emissor}
                onChange={(e) => handleDocChange('orgao_emissor', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Município/UF *</label>
              <input
                type="text"
                required
                value={document.municipio_uf}
                onChange={(e) => handleDocChange('municipio_uf', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Processo administrativo *</label>
              <input
                type="text"
                required
                value={document.processo_administrativo}
                onChange={(e) => handleDocChange('processo_administrativo', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Data de emissão *</label>
              <input
                type="date"
                required
                value={toDateInputValue(document.data_emissao)}
                onChange={(e) => handleDocChange('data_emissao', e.target.value || '')}
              />
            </div>
            <div className="form-group">
              <label>Validade - data fim *</label>
              <input
                id="input-validade-data-fim"
                type="date"
                required
                value={toDateInputValue(document.validade?.data_fim)}
                onChange={(e) => handleValidadeChange('data_fim', e.target.value || '')}
              />
            </div>
            <div className="form-group">
              <label>Validade - observação</label>
              <input
                type="text"
                value={document.validade?.observacao ?? ''}
                onChange={(e) => handleValidadeChange('observacao', e.target.value)}
              />
            </div>
          </div>
          <div className="section-form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>Salvar</button>
          </div>
        </form>
      </details>

      <details className="extracao-section collapsible-section" aria-labelledby="sec-empreendimento" open>
        <summary>
          <h2 id="sec-empreendimento">Empreendimento</h2>
        </summary>
        <form onSubmit={handleSalvarEmpreendimento}>
          <div className="empreendimento-select-section">
            <div className="form-group">
              <label>Empreendimento vinculado</label>
              <select
                value={empreendimentoId}
                onChange={(e) => setEmpreendimentoId(e.target.value)}
                className="empreendimento-select"
              >
                <option value="">Selecione o empreendimento</option>
                {empreendimentos.map((emp) => (
                  <option key={emp.id} value={emp.id}>
                    {emp.nome} {emp.cnpj_cpf ? ` — ${emp.cnpj_cpf}` : ''} {emp.municipio ? ` — ${emp.municipio}` : ''}
                  </option>
                ))}
              </select>
            </div>
            {empreendimentos.length === 0 && (
              <p className="empreendimento-empty-msg">Nenhum empreendimento cadastrado. <Link to="/empreendimentos">Crie um empreendimento</Link> primeiro.</p>
            )}
            {empreendimentoId && empreendimentos.length > 0 && (() => {
              const emp = empreendimentos.find((e) => e.id === empreendimentoId);
              if (!emp) return null;
              return (
                <div className="empreendimento-dados">
                  <h3>Dados do empreendimento selecionado</h3>
                  <div className="empreendimento-dados-grid">
                    <div><strong>Nome:</strong> {emp.nome}</div>
                    {emp.cnpj_cpf && <div><strong>CNPJ/CPF:</strong> {emp.cnpj_cpf}</div>}
                    {emp.municipio && <div><strong>Município:</strong> {emp.municipio}</div>}
                    {emp.descricao && <div><strong>Descrição:</strong> {emp.descricao}</div>}
                  </div>
                  <Link to={`/empreendimentos/${emp.id}`} className="btn btn-link-empreendimento">
                    <FiExternalLink /> Ver detalhes do empreendimento
                  </Link>
                </div>
              );
            })()}
          </div>
          <div className="section-form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>Salvar</button>
          </div>
        </form>
      </details>

      <details className="extracao-section condicionantes-section collapsible-section" aria-labelledby="sec-condicionantes">
        <summary>
          <h2 id="sec-condicionantes">Condicionantes</h2>
          <span className="collapsible-badge">{condicionantes.length} condicionante(s)</span>
        </summary>
        {condicoesCarregando ? (
          <div className="loading condicionantes-loading" aria-busy="true" style={{ padding: '1rem' }}>
            <p>Aguardando condicionantes extraídas do PDF...</p>
            {mensagemEtapa && <p className="licenca-processing-step-inline">{mensagemEtapa}</p>}
          </div>
        ) : (
        <form onSubmit={handleConcluirValidacao}>
          <div className="condicionantes-section-header">
            <div>
              <p className="section-help">Revise e edite cada condicionante. Marque &quot;Gerar monitoramento&quot; nas que devem ter prazos e alertas por e-mail.</p>
            </div>
            <button type="button" className="btn btn-add-condicionante" onClick={handleAdicionarCondicionante}>
              <FiPlus /> Adicionar condicionante
            </button>
          </div>
          <div className="condicionantes-cards">
            {condicionantes.map((c, idx) => (
              <article
                key={c.id}
                className="condicionante-card"
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  const target = e.currentTarget;
                  const rect = target.getBoundingClientRect();
                  const midY = rect.top + rect.height / 2;
                  target.classList.toggle('condicionante-drag-over-top', e.clientY < midY);
                  target.classList.toggle('condicionante-drag-over-bottom', e.clientY >= midY);
                }}
                onDragLeave={(e) => {
                  e.currentTarget.classList.remove('condicionante-drag-over-top', 'condicionante-drag-over-bottom');
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.currentTarget.classList.remove('condicionante-drag-over-top', 'condicionante-drag-over-bottom');
                  const dragIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                  const rect = e.currentTarget.getBoundingClientRect();
                  const midY = rect.top + rect.height / 2;
                  const dropIdx = e.clientY < midY ? idx : idx + 1;
                  if (dragIdx !== dropIdx && dragIdx !== dropIdx - 1) {
                    const toIdx = dragIdx < dropIdx ? dropIdx - 1 : dropIdx;
                    handleDragCondicionante(dragIdx, toIdx);
                  }
                }}
              >
                <div className="condicionante-card-header">
                  <div
                    className="condicionante-card-drag"
                    title="Arrastar para reordenar"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('text/plain', String(idx));
                      e.dataTransfer.effectAllowed = 'move';
                      e.currentTarget.closest('.condicionante-card')?.classList.add('condicionante-dragging');
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.closest('.condicionante-card')?.classList.remove('condicionante-dragging');
                    }}
                  >
                    <FiMove />
                  </div>
                  <div className="condicionante-card-num">
                    <label>Nº</label>
                    <input
                      type="text"
                      value={c.condicionante_numero ?? ''}
                      onChange={(e) => handleCondChange(idx, 'condicionante_numero', e.target.value)}
                      placeholder="Ex: 1, 2.1"
                    />
                  </div>
                  <div className="condicionante-card-tipo">
                    <label>Tipo</label>
                    <select
                      value={(c.tipo_condicao ?? c.tipo) === 'especifica' ? 'especifica' : 'geral'}
                      onChange={(e) => handleCondChange(idx, 'tipo_condicao', e.target.value)}
                      className="condicionante-tipo-select"
                    >
                      <option value="geral">Geral</option>
                      <option value="especifica">Específica</option>
                    </select>
                  </div>
                  <button
                    type="button"
                    className="btn-remove-condicionante"
                    onClick={() => handleRemoverCondicionante(idx)}
                    onMouseDown={(e) => e.stopPropagation()}
                    title="Remover condicionante"
                  >
                    <FiTrash2 />
                  </button>
                </div>
                <div className="condicionante-card-field">
                  <label>Texto da condicionante</label>
                  <textarea
                    value={c.texto ?? ''}
                    onChange={(e) => handleCondChange(idx, 'texto', e.target.value)}
                    rows={4}
                    placeholder="Descrição completa da condicionante"
                  />
                </div>
                <div className="condicionante-card-monitoramento">
                  <div className="condicionante-card-gerar">
                    <label>
                      <input
                        type="checkbox"
                        checked={!!c.gerar_monitoramento}
                        onChange={(e) => handleCondChange(idx, 'gerar_monitoramento', e.target.checked)}
                      />
                      Gerar monitoramento e prazos
                    </label>
                  </div>
                  {c.gerar_monitoramento && (
                    <div className="condicionante-card-monitoramento-detalhes">
                      <div className="condicionante-card-field-inline">
                        <label>Data do primeiro monitoramento</label>
                        <input
                          type="date"
                          value={toDateInputValue(c.primeiro_monitoramento?.first_due_date)}
                          onChange={(e) => handleCondPrimeiroMonitoramentoChange(idx, 'first_due_date', e.target.value || null)}
                        />
                      </div>
                      <div className="condicionante-card-field-inline">
                        <label>Ou texto do prazo (ex: até 30 dias após emissão)</label>
                        <input
                          type="text"
                          value={c.primeiro_monitoramento?.first_due_text ?? ''}
                          onChange={(e) => handleCondPrimeiroMonitoramentoChange(idx, 'first_due_text', e.target.value || null)}
                          placeholder="Ex: até 30 dias após a emissão"
                        />
                      </div>
                      <div className="condicionante-card-field-inline">
                        <label>Frequência</label>
                        <select
                          value={c.frequencia ?? ''}
                          onChange={(e) => handleCondChange(idx, 'frequencia', e.target.value || null)}
                        >
                          <option value="">—</option>
                          <option value="unica">Única</option>
                          <option value="diario">Diário</option>
                          <option value="semanal">Semanal</option>
                          <option value="quinzenal">Quinzenal</option>
                          <option value="mensal">Mensal</option>
                          <option value="bimestral">Bimestral</option>
                          <option value="trimestral">Trimestral</option>
                          <option value="semestral">Semestral</option>
                          <option value="anual">Anual</option>
                          <option value="por_evento">Por evento</option>
                          <option value="continuo">Contínuo</option>
                          <option value="sob_demanda">Sob demanda</option>
                          <option value="nao_informado">Não informado</option>
                        </select>
                      </div>
                      <span className="condicionante-card-dias">
                        Avisar
                        <input
                          type="number"
                          min={1}
                          max={365}
                          value={c.alertas_dias_antes ?? 7}
                          onChange={(e) => handleCondChange(idx, 'alertas_dias_antes', parseInt(e.target.value, 10) || 7)}
                        />
                        dias antes
                      </span>
                      <div className="condicionante-card-prioridade">
                        <label>Prioridade</label>
                        <select
                          value={c.prioridade ?? 'media'}
                          onChange={(e) => handleCondChange(idx, 'prioridade', e.target.value)}
                        >
                          <option value="baixa">Baixa</option>
                          <option value="media">Média</option>
                          <option value="alta">Alta</option>
                        </select>
                      </div>
                      <div className="condicionante-card-responsaveis condicionante-responsaveis-trello">
                        <label><FiUser /> Responsáveis pelo cumprimento</label>
                        {!empreendimentoId ? (
                          <p className="condicionante-responsaveis-hint">Selecione o empreendimento acima para escolher responsáveis. Os prazos criados terão os mesmos responsáveis.</p>
                        ) : usuariosEmpreendimento.length === 0 ? (
                          <p className="condicionante-responsaveis-hint">Nenhum usuário vinculado ao empreendimento. Vincule usuários em &quot;Gerenciar usuários&quot; do empreendimento.</p>
                        ) : (
                          <div className="membros-trello">
                            {(c.responsaveis_ids || []).length > 0 ? (
                              (c.responsaveis_ids || []).map((uid) => {
                                const u = usuariosEmpreendimento.find((x) => x.id === uid);
                                return (
                                  <div
                                    key={uid}
                                    className="membro-avatar membro-avatar-clickable"
                                    title={u ? `Remover ${u.nome}` : 'Remover'}
                                    onClick={() => handleCondChange(idx, 'responsaveis_ids', (c.responsaveis_ids || []).filter((id) => id !== uid))}
                                    role="button"
                                  >
                                    {(u && u.nome ? u.nome : '?').charAt(0).toUpperCase()}
                                  </div>
                                );
                              })
                            ) : (
                              <span className="membro-empty">Sem responsáveis</span>
                            )}
                            {addResponsavelCondIdx !== idx ? (
                              (c.responsaveis_ids || []).length < usuariosEmpreendimento.length && (
                                <button
                                  type="button"
                                  className="membro-add-btn"
                                  onClick={() => { setAddResponsavelCondIdx(idx); setAddResponsavelId(''); }}
                                  title="Adicionar responsável"
                                >
                                  +
                                </button>
                              )
                            ) : (
                              <div className="delegar-form-trello">
                                <select
                                  value={addResponsavelId}
                                  onChange={(e) => setAddResponsavelId(e.target.value)}
                                  className="delegar-select"
                                >
                                  <option value="">Selecione...</option>
                                  {usuariosEmpreendimento
                                    .filter((u) => !(c.responsaveis_ids || []).includes(u.id))
                                    .map((usr) => (
                                      <option key={usr.id} value={usr.id}>{usr.nome}{usr.role_nome ? ` (${usr.role_nome})` : ''}</option>
                                    ))}
                                </select>
                                <div className="delegar-actions">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-primary"
                                    onClick={() => {
                                      if (!addResponsavelId) return;
                                      const atuais = c.responsaveis_ids || [];
                                      if (atuais.includes(addResponsavelId)) {
                                        setAddResponsavelCondIdx(null);
                                        setAddResponsavelId('');
                                        return;
                                      }
                                      handleCondChange(idx, 'responsaveis_ids', [...atuais, addResponsavelId]);
                                      setAddResponsavelCondIdx(null);
                                      setAddResponsavelId('');
                                    }}
                                  >
                                    Adicionar
                                  </button>
                                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => { setAddResponsavelCondIdx(null); setAddResponsavelId(''); }}>Cancelar</button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
          {condicionantes.length === 0 && (
            <p className="text-muted">Nenhuma condicionante extraída.</p>
          )}

          <details className="extracao-section condicionantes-monitoradas-section collapsible-section" aria-labelledby="sec-condicionantes-monitoradas">
          <summary>
            <h2 id="sec-condicionantes-monitoradas">Condicionantes com prazos</h2>
            <span className="collapsible-badge">
              {condicionantes.filter((c) => c.gerar_monitoramento).length} condicionante(s)
            </span>
          </summary>
          <p className="section-help">Lista das condicionantes que terão prazos de monitoramento e alertas por e-mail.</p>
          {condicionantes.filter((c) => c.gerar_monitoramento).length === 0 ? (
            <p className="text-muted">Nenhuma condicionante com monitoramento ativo. Marque &quot;Gerar monitoramento e prazos&quot; nas condicionantes desejadas.</p>
          ) : (
            <div className="condicionantes-monitoradas-list">
              {condicionantes
                .filter((c) => c.gerar_monitoramento)
                .map((c, idx) => (
                  <div key={c.id || idx} className="condicionante-monitorada-item">
                    <div className="condicionante-monitorada-header">
                      <span className="condicionante-monitorada-num">{c.condicionante_numero ?? '-'}</span>
                      <span className="condicionante-monitorada-tema">{c.tema || 'Sem tema'}</span>
                      <span className="condicionante-monitorada-badges">
                        <span className="badge-tipo">{(c.tipo_condicao ?? c.tipo) === 'especifica' ? 'Específica' : 'Geral'}</span>
                        <span className="badge-freq">{c.frequencia || 'Não informado'}</span>
                        <span className="badge-prioridade">{c.prioridade || 'Média'}</span>
                      </span>
                    </div>
                    <p className="condicionante-monitorada-resumo">
                      {(c.texto || c.descricao || '').substring(0, 200)}
                      {(c.texto || c.descricao || '').length > 200 ? '…' : ''}
                    </p>
                    <div className="condicionante-monitorada-meta">
                      {c.primeiro_monitoramento?.first_due_date && (
                        <span>1º monitoramento: {toDateInputValue(c.primeiro_monitoramento.first_due_date)}</span>
                      )}
                      {c.primeiro_monitoramento?.first_due_text && (
                        <span>Prazo: {c.primeiro_monitoramento.first_due_text}</span>
                      )}
                      {c.alertas_dias_antes != null && (
                        <span>Alertas: {c.alertas_dias_antes} dias antes</span>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
          </details>

          <div className="section-form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate('/licencas')}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Salvando...' : 'Concluir validação e criar monitoramento'}
            </button>
          </div>
        </form>
        )}
      </details>

      <details className="extracao-section prazos-section collapsible-section" aria-labelledby="sec-prazos">
        <summary>
          <h2 id="sec-prazos">Prazos da licença</h2>
          <span className="collapsible-badge">{tarefas.length} prazo(s)</span>
        </summary>
        <p className="section-help">Todos os prazos de monitoramento gerados em vínculo com esta licença.</p>
        {tarefas.length === 0 ? (
            <p className="text-muted">Nenhum prazo gerado ainda. Conclua a validação para criar os prazos de monitoramento.</p>
          ) : (
            <div className="prazos-table-wrapper">
              <table className="prazos-table">
                <thead>
                  <tr>
                    <th>Condicionante</th>
                    <th>Título</th>
                    <th>Vencimento</th>
                    <th>Status</th>
                    <th>Prioridade</th>
                    <th>Responsável</th>
                    <th>Recorrência</th>
                  </tr>
                </thead>
                <tbody>
                  {tarefas.map((t) => (
                    <tr key={t.id}>
                      <td>
                        <span className="prazos-cond-num">{t.condicionante_numero ?? '-'}</span>
                        {t.tema && <span className="prazos-cond-tema">{t.tema}</span>}
                      </td>
                      <td>{t.titulo || '-'}</td>
                      <td>{t.data_vencimento ? toDateInputValue(t.data_vencimento) : '-'}</td>
                      <td>
                        <span className={`badge-status badge-${(t.status || 'pendente').replace(/_/g, '-')}`}>
                          {(t.status || 'pendente').replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td>
                        <span className={`badge-prioridade badge-prioridade-${t.prioridade || 'media'}`}>
                          {t.prioridade || 'Média'}
                        </span>
                      </td>
                      <td>{t.responsavel_nome || '-'}</td>
                      <td>
                        {t.recorrente ? (t.frequencia || 'Recorrente') : 'Única'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </details>

      <details className="extracao-section documentos-licenca-section collapsible-section" aria-labelledby="sec-documentos">
        <summary>
          <h2 id="sec-documentos">Documentos vinculados</h2>
          <span className="collapsible-badge">{documentos.length} documento(s)</span>
        </summary>
        <p className="section-help">Documentos anexados a esta licença.</p>
        <DocumentosSection licencaId={id} documentos={documentos} onRefresh={carregarDocumentos} onDocumentAdded={(doc) => setDocumentos(prev => [...prev, doc])} />
      </details>

      <details className="extracao-section extracao-debug-section collapsible-section">
          <summary>
            <h2>Histórico da licença</h2>
            <span className="collapsible-badge">
              {historico.length} registro(s)
              {(textoExtraido || Object.keys(extracaoLog).length > 0) && ' · Debug'}
            </span>
          </summary>
          <p className="section-help">Registro de alterações em dados da licença, condicionantes, monitoramentos, prazos e controle de acesso.</p>
          {historico.length === 0 ? (
            <p className="text-muted">Nenhum registro no histórico ainda.</p>
          ) : (
            <div className="historico-list">
              {historico.map((h) => (
                <details key={h.id} className="historico-item">
                  <summary>
                    <span className="historico-data">{new Date(h.criado_em).toLocaleString('pt-BR')}</span>
                    <span className="historico-usuario">{h.usuario_nome || 'Sistema'}</span>
                    <span className="historico-descricao">{h.descricao}</span>
                    <span className="historico-entidade">{h.entidade}</span>
                  </summary>
                  <div className="historico-item-content">
                    {h.entidade === 'documentos' && (() => {
                      const parse = (v) => {
                        if (!v) return null;
                        if (typeof v === 'object') return v;
                        try { return JSON.parse(v); } catch (_) { return v; }
                      };
                      const antes = parse(h.dados_anteriores);
                      const depois = parse(h.dados_novos);
                      const arquivo = depois?.arquivo || antes?.arquivo;
                      const titulo = depois?.titulo || antes?.titulo;
                      return arquivo ? (
                        <a
                          href={`${getDownloadBaseUrl()}${arquivo}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-primary historico-btn-download"
                        >
                          <FiDownload /> Baixar documento{ titulo ? `: ${titulo}` : '' }
                        </a>
                      ) : null;
                    })()}
                    <pre className="debug-pre">
                      {(() => {
                        const parse = (v) => {
                          if (!v) return null;
                          if (typeof v === 'object') return v;
                          try { return JSON.parse(v); } catch (_) { return v; }
                        };
                        const fmt = (v) => (typeof v === 'object' ? JSON.stringify(v, null, 2) : String(v));
                        const antes = parse(h.dados_anteriores);
                        const depois = parse(h.dados_novos);
                        if (antes && depois) return `Antes: ${fmt(antes)}\n\nDepois: ${fmt(depois)}`;
                        if (antes) return `Antes: ${fmt(antes)}`;
                        if (depois) return `Depois: ${fmt(depois)}`;
                        return '(sem detalhes)';
                      })()}
                    </pre>
                  </div>
                </details>
              ))}
            </div>
          )}
          {(textoExtraido || Object.keys(extracaoLog).length > 0) && (
            <div className="historico-debug-section">
              <h3>Debug da extração</h3>
              {extracaoLog.uso_ia?.totais && (
                <details className="debug-details" open>
                  <summary>Uso da IA e custo estimado</summary>
                  <div className="condicionantes-iniciais-meta">
                    <span>Chamadas: {extracaoLog.uso_ia.totais.chamadas ?? 0}</span>
                    <span>Modelo(s): {(extracaoLog.uso_ia.totais.modelos || []).join(', ') || '-'}</span>
                    <span>Entrada: {extracaoLog.uso_ia.totais.tokens_entrada ?? 0} tokens</span>
                    <span>Entrada em cache: {extracaoLog.uso_ia.totais.tokens_entrada_cache ?? 0} tokens</span>
                    <span>Saída: {extracaoLog.uso_ia.totais.tokens_saida ?? 0} tokens</span>
                    <span>Total: {extracaoLog.uso_ia.totais.tokens_total ?? 0} tokens</span>
                    <span>
                      Custo estimado: US$ {Number(extracaoLog.uso_ia.totais.custo_estimado_usd || 0).toFixed(6)}
                      {extracaoLog.uso_ia.totais.chamadas_sem_preco > 0 ? ' (parcial: configure o preço do modelo)' : ''}
                    </span>
                  </div>
                  <p className="section-help">{extracaoLog.uso_ia.aviso}</p>
                  <pre className="debug-pre">{JSON.stringify(extracaoLog.uso_ia.chamadas || [], null, 2)}</pre>
                </details>
              )}
              {extracaoLog.condicionantes_iniciais && Array.isArray(extracaoLog.condicionantes_iniciais) && extracaoLog.condicionantes_iniciais.length > 0 && (
                <details className="debug-details" open>
                  <summary>Condicionantes iniciais extraídas ({extracaoLog.condicionantes_iniciais.length} condicionante(s))</summary>
                  <div className="condicionantes-iniciais-list">
                    {extracaoLog.condicionantes_iniciais.map((c, idx) => (
                      <div key={c.id || idx} className="condicionante-inicial-item">
                        <div className="condicionante-inicial-header">
                          <span className="condicionante-inicial-num">Nº {c.condicionante_numero ?? (idx + 1)}</span>
                          {c.tema && <span className="condicionante-inicial-tema">{c.tema}</span>}
                          {c.tipo && <span className="condicionante-inicial-tipo">{c.tipo}</span>}
                        </div>
                        <p className="condicionante-inicial-texto">{(c.texto || '').substring(0, 500)}{(c.texto || '').length > 500 ? '…' : ''}</p>
                        {(c.frequencia || c.primeiro_monitoramento) && (
                          <div className="condicionante-inicial-meta">
                            {c.frequencia && <span>Frequência: {c.frequencia}</span>}
                            {c.primeiro_monitoramento?.first_due_date && <span>1º monitoramento: {toDateInputValue(c.primeiro_monitoramento.first_due_date)}</span>}
                            {c.primeiro_monitoramento?.first_due_text && <span>Prazo: {c.primeiro_monitoramento.first_due_text}</span>}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {textoExtraido && (
                <details className="debug-details">
                  <summary>Texto extraído do PDF ({textoExtraido.length} caracteres)</summary>
                  <pre className="debug-pre">{textoExtraido || '(vazio)'}</pre>
                </details>
              )}
              {Object.keys(extracaoLog).length > 0 && (
                <details className="debug-details">
                  <summary>Log completo da extração</summary>
                  <pre className="debug-pre">{JSON.stringify(extracaoLog, null, 2)}</pre>
                </details>
              )}
            </div>
          )}
        </details>
    </div>
  );
};

export default ValidarExtracao;
